/**
 * "You're Never Locked In" Section
 * 
 * Simplified to focus on the seed phrase reveal feature with a video demo.
 * Key messaging: View your seed phrase anytime on device, true ownership.
 */

import { useRef, useState, useEffect } from 'react';
import { Key, ChevronRight, Check, Eye } from 'lucide-react';

const SEED_PHRASE_VIDEO = 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/seed-phrase_79f31245.mov';

export default function NeverLockedInSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          videoRef.current?.play().catch(() => {});
        } else {
          setIsVisible(false);
          if (videoRef.current) {
            videoRef.current.pause();
          }
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 bg-[#f7f7f8] overflow-hidden">
      <div className="container px-4 max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#6a63f5]/5 border border-[#6a63f5]/10 rounded-full px-4 py-1.5 mb-6">
            <Key className="w-3.5 h-3.5 text-[#6a63f5]" />
            <span className="text-xs font-medium text-[#6a63f5]/70 uppercase tracking-wider">True Ownership</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 tracking-tight text-gray-900">
            You're never locked in
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Your seed phrase is always accessible. View it directly on your Ryder One device, anytime you need it.
          </p>
        </div>

        {/* Content: Video + Text */}
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              {/* Left: Video */}
              <div className="lg:w-1/2 bg-[#0a0a0f] flex items-center justify-center overflow-hidden">
                <video
                  ref={videoRef}
                  src={SEED_PHRASE_VIDEO}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Right: Details */}
              <div className="lg:w-1/2 p-6 sm:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-amber-500/10">
                    <Eye className="w-5 h-5 text-amber-500" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                    View Seed Phrase
                  </h3>
                </div>

                <p className="text-gray-500 mb-6 text-base leading-relaxed">
                  Want a traditional backup alongside TapSafe? View your seed phrase anytime directly on your Ryder One. It never leaves the device — it's always your choice.
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    'View your 24-word seed phrase on the device screen',
                    'Use alongside TapSafe Recovery for extra peace of mind',
                    'Write it down, stamp it in metal — your call',
                    'Available anytime from device settings',
                  ].map((detail, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-amber-500/10">
                        <Check className="w-3 h-3 text-amber-500" />
                      </div>
                      <span className="text-sm text-gray-600">{detail}</span>
                    </li>
                  ))}
                </ul>

                <div>
                  <button
                    onClick={() => {
                      const pricingSection = document.getElementById('hero-pricing');
                      if (pricingSection) {
                        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      } else {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    className="inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-full bg-gray-900 text-white hover:bg-gray-800 transition-all duration-300 hover:scale-105"
                    style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}
                  >
                    Get Ryder One
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
